import React from 'react';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import AdminNavbar from './AdminNavbar';
import {
  Send, Rocket, Loader2, MessageSquare, PanelLeftClose, PanelLeft,
} from 'lucide-react';
import { useAppStudio } from './appstudio/useAppStudio';
import { AppStudioSidebar } from './appstudio/AppStudioSidebar';
import { ConversationView } from './appstudio/ConversationView';
import { EmergentPromptCard } from './appstudio/EmergentPromptCard';

const AppStudio = () => {
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const studio = useAppStudio({ isAuthenticated, authLoading });

  // Loading state
  if (authLoading) {
    return (
      <div className="min-h-screen bg-[#1a1c1b]">
        <AdminNavbar />
        <div className="flex items-center justify-center" style={{ height: 'calc(100vh - 80px)' }}>
          <Loader2 className="w-10 h-10 text-[#7dd3fc] animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#1a1c1b] flex flex-col">
      <AdminNavbar />

      <div className="flex-1 flex overflow-hidden">
        {/* Sidebar */}
        <AppStudioSidebar
          sidebarOpen={studio.sidebarOpen}
          isLoadingProjects={studio.isLoadingProjects}
          savedProjects={studio.savedProjects}
          currentIdea={studio.currentIdea}
          onNew={studio.startNew}
          onLoadProject={studio.loadProject}
          onDeleteProject={studio.deleteProject}
        />

        {/* Toggle Sidebar Button */}
        <button
          onClick={() => studio.setSidebarOpen(!studio.sidebarOpen)}
          data-testid="toggle-sidebar-btn"
          className="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-[#302f2c] border border-[#1e3a5f]/50 rounded-r-lg p-2 hover:bg-[#1e3a5f]/30 transition-colors"
          style={{ marginLeft: studio.sidebarOpen ? '288px' : '0' }}
        >
          {studio.sidebarOpen ? (
            <PanelLeftClose className="w-4 h-4 text-[#888680]" />
          ) : (
            <PanelLeft className="w-4 h-4 text-[#888680]" />
          )}
        </button>

        {/* Main Content */}
        <main className="flex-1 flex flex-col max-w-4xl mx-auto w-full px-4 py-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-[#7dd3fc] to-[#38bdf8] rounded-xl flex items-center justify-center">
                <Rocket className="w-5 h-5 text-[#1a1c1b]" />
              </div>
              <div>
                <h1 className="text-[#7dd3fc] font-bold text-xl md:text-2xl">App Studio</h1>
                <p className="text-[#888680] text-xs">
                  {studio.currentIdea ? studio.currentIdea.name : 'Describe your app idea'}
                </p>
              </div>
            </div>
          </div>

          {/* Conversation Area */}
          <div className="flex-1 overflow-y-auto mb-4 space-y-6 min-h-[250px]" data-testid="conversation-area">
            <ConversationView
              conversation={studio.conversation}
              messagesEndRef={studio.messagesEndRef}
              expandedSections={studio.expandedSections}
              toggleSection={studio.toggleSection}
              copiedItems={studio.copiedItems}
              copyToClipboard={studio.copyToClipboard}
              onFollowUp={studio.handleFollowUp}
              isProcessing={studio.isProcessing}
              onExampleClick={studio.setPrompt}
            />
          </div>

          {/* Emergent Prompt Section */}
          {studio.currentIdea && (
            <EmergentPromptCard
              prompt={studio.emergentPrompt}
              copiedItems={studio.copiedItems}
              copyToClipboard={studio.copyToClipboard}
            />
          )}

          {/* Input Area */}
          <div className="sticky bottom-0 bg-[#1a1c1b] pt-3 border-t border-[#1e3a5f]/30">
            {studio.currentIdea && (
              <p className="text-center text-[#888680] text-xs mb-2">
                <MessageSquare className="inline w-3 h-3 mr-1" />
                Continue refining or generate more code
              </p>
            )}

            <form onSubmit={studio.handleSubmit} className="relative">
              <Textarea
                value={studio.prompt}
                onChange={(e) => studio.setPrompt(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    studio.handleSubmit(e);
                  }
                }}
                placeholder={studio.currentIdea
                  ? 'Add features, ask for more code...'
                  : 'Describe the app you want to build...'}
                rows={2}
                disabled={studio.isProcessing}
                data-testid="app-studio-prompt-input"
                className="bg-[#302f2c] border-[#1e3a5f] text-[#7dd3fc] placeholder:text-[#888680]/50 focus:border-[#7dd3fc] resize-none pr-14 rounded-xl text-sm"
              />
              <Button
                type="submit"
                disabled={!studio.prompt.trim() || studio.isProcessing}
                data-testid="app-studio-submit-btn"
                className="absolute right-2 bottom-2 bg-[#7dd3fc] text-[#1a1c1b] hover:bg-[#7dd3fc]/90 rounded-full w-9 h-9 p-0"
              >
                {studio.isProcessing ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Send className="w-4 h-4" />
                )}
              </Button>
            </form>
          </div>
        </main>
      </div>
    </div>
  );
};

export default AppStudio;
